/**
 * @file AttachmentListActions.tsx
 * @description Preview, download, Extract schedule, email and share controls
 * for one attachment version. Consumer: `AttachmentList`, once per file row and once per listed
 * version.
 *
 * A save is disposable — the retention policy may remove it — so a share link
 * or an email built from one would break. Creating either is therefore disabled
 * when this version is a save (`kind === 'save'`; absence means published).
 * Download and preview stay enabled: they act on the file's current bytes,
 * which is exactly what a save is. An existing link on a save stays revocable
 * and copyable, because refusing that would strand a link already in the wild.
 *
 * The reason given for that block depends on `isCurrentVersion`, because only
 * the current save can act on it: `publishAttachmentVersion` refuses any version
 * that is not the artifact's `currentVersionId`. The current save is told to
 * publish first; an older save is told the rule and pointed at Download, which
 * is the one action still open to it.
 *
 * Sharing is blocked for a second, independent reason (SYD-2378 R3): a link
 * hands bytes to someone outside the app, so it needs one whole Storage object,
 * and a `chunked` version has none. Email is deliberately NOT blocked for a
 * chunked version — it assembles the file in the browser and attaches the bytes,
 * so it works — which is why the two reasons are separate values rather than one.
 *
 * Sharing is blocked for a third: `inArtifactMap` false means the strip lists
 * this version from `attachments/{fileUid}` while the artifact's own map has
 * never held it, and every share path — create, revoke, copy — resolves against
 * that map. This component takes the resolved `version` rather than looking one
 * up, so "I could not find it" can never read here as "nothing blocks it": that
 * inversion is what once left Share and Email enabled on every desktop save
 * (SYD-2455).
 *
 * Extract schedule follows Download and, like it, is not disabled by a read-only
 * panel: the approval is written server-side as the reviewer. Its file type is
 * read from the version's own file name, and its blocked reason comes from
 * `resolveExtractScheduleBlockedReason`. It is absent in the PDF export portal,
 * because a control printed onto a rendered document is a defect, not a state.
 */

import { FC, useCallback } from 'react';
import { Button, Typography, Popover, Tooltip, Tag } from 'antd';
import {
  DownloadOutlined,
  LinkOutlined,
  StopOutlined,
  MailOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';

import { AttachmentVersion } from '#types/System.types';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { ExtractScheduleButton } from './ExtractScheduleButton';
import { resolveAttachmentFileType } from './resolveAttachmentFileType';
import { resolveExtractScheduleBlockedReason } from './resolveExtractScheduleBlockedReason';
import type { ExtractScheduleHost } from './index.types';

const { Text } = Typography;

/**
 * Props for AttachmentListActions component
 */
interface AttachmentListActionsProps {
  /** File unique identifier */
  fileUid: string;
  /** Version identifier */
  versionId: string;
  /** File name */
  fileName: string;
  /** The version these controls act on, resolved by the caller that lists it */
  version: AttachmentVersion;
  /**
   * Whether the artifact's own attachments map holds `versionId`. False for a
   * version the strip lists from `attachments/{fileUid}` alone, which every
   * share path would fail to resolve.
   */
  inArtifactMap: boolean;
  /**
   * Whether `versionId` is the file's `currentVersionId`. Only that version can
   * be published, so it decides which blocked-action reason a save is given.
   */
  isCurrentVersion: boolean;
  /** Whether any operation is currently in progress */
  anyOperationInProgress: boolean;
  /** Whether the component is disabled */
  disabled: boolean;
  /** Handler for downloading a specific version */
  onVersionDownload: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for sharing a version */
  onShareVersion: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** Handler for unsharing a version */
  onUnshareVersion: (fileUid: string, versionId: string) => Promise<void>;
  /** Handler for copying share link */
  onCopyShareLink: (fileUid: string, versionId: string) => Promise<void>;
  /** Handler for previewing a version */
  onPreviewVersion?: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => void;
  /** Handler for emailing a version */
  onEmailVersion?: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
  /** The panel's host, which decides whether Extract schedule can write to a form. */
  extractScheduleHost: ExtractScheduleHost;
  /** The user holds aiOperator or aiAdmin. */
  canExtractSchedule: boolean;
  /** The version an extraction is reading, or null. */
  extractingVersionId: string | null;
  /** Handler for extracting a schedule from a version. */
  onExtractSchedule: (
    fileUid: string,
    versionId: string,
    fileName: string,
  ) => Promise<void>;
}

/**
 * Component for rendering attachment version actions (share and download buttons)
 */
export const AttachmentListActions: FC<AttachmentListActionsProps> = ({
  fileUid,
  versionId,
  fileName,
  version,
  inArtifactMap,
  isCurrentVersion,
  anyOperationInProgress,
  disabled,
  onVersionDownload,
  onShareVersion,
  onUnshareVersion,
  onCopyShareLink,
  onPreviewVersion,
  onEmailVersion,
  extractScheduleHost,
  canExtractSchedule,
  extractingVersionId,
  onExtractSchedule,
}) => {
  const { isPdfExportMode } = usePdfExportContext();
  const extractScheduleBlockedReason = resolveExtractScheduleBlockedReason({
    fileType: resolveAttachmentFileType(
      version.versionFileName ?? fileName,
      version.type,
    ),
    host: extractScheduleHost,
    canExtract: canExtractSchedule,
  });
  const isShared = version.share?.isShared === true;
  const saveBlockedReason =
    version.kind !== 'save'
      ? null
      : isCurrentVersion
        ? 'Publish this save first. A share link and an email must point at a published version.'
        : 'Only the current version can be published, so this save cannot be shared or emailed. Download it here instead.';
  /**
   * Every share path resolves the version against the artifact's own map, so a
   * version listed from the attachment document alone can neither be shared
   * nor have an existing link revoked or copied from here. Download and email
   * read the attachment document itself, so both stay open to it.
   */
  const unresolvedReason = inArtifactMap
    ? null
    : 'This document lists this version but does not hold it, so it cannot be shared from here. Download it here instead.';
  /**
   * Sharing additionally needs a whole object to hand out, which a chunked
   * version does not have. Email is unaffected: it assembles the version in the
   * browser and attaches the bytes, so it is left enabled.
   */
  const shareBlockedReason =
    saveBlockedReason ??
    unresolvedReason ??
    (version.layout === 'chunked'
      ? 'This version is stored in chunks, so it has no share link. Download it here instead.'
      : null);

  /**
   * Formats the expiration date for display
   */
  const formatExpirationDate = useCallback(
    (share: NonNullable<AttachmentVersion['share']> | undefined | null) => {
      if (!share || !share.expiration) return '-';

      if (share.expiration instanceof Timestamp) {
        return share.expiration.toDate().toLocaleString();
      }

      if (
        typeof share.expiration === 'object' &&
        'seconds' in share.expiration &&
        typeof share.expiration.seconds === 'number'
      ) {
        return new Date(share.expiration.seconds * 1000).toLocaleString();
      }

      return 'No expiration';
    },
    [],
  );

  return (
    <FlexBox noGrow gap={5}>
      {onPreviewVersion && (
        <Tooltip title={`Preview ${fileName}`}>
          <Button
            size="small"
            type="text"
            icon={<EyeOutlined />}
            onClick={() => onPreviewVersion(fileUid, versionId, fileName)}
            disabled={anyOperationInProgress}
            id={`attachment-preview-button-${fileUid}-${versionId}`}
            aria-label={`Preview ${fileName}`}
          />
        </Tooltip>
      )}
      <Button
        size="small"
        type="text"
        icon={<DownloadOutlined />}
        onClick={() => onVersionDownload(fileUid, versionId, fileName)}
        disabled={anyOperationInProgress}
        id={`attachment-download-button-${fileUid}-${versionId}`}
        aria-label={`Download ${fileName}`}
      />
      {!isPdfExportMode && (
        <ExtractScheduleButton
          id={`attachment-extract-schedule-button-${fileUid}-${versionId}`}
          fileName={fileName}
          blockedReason={extractScheduleBlockedReason}
          busy={anyOperationInProgress}
          loading={extractingVersionId === versionId}
          onClick={() => void onExtractSchedule(fileUid, versionId, fileName)}
        />
      )}
      {onEmailVersion && (
        <Tooltip title={saveBlockedReason ?? `Email ${fileName}`}>
          <span style={{ display: 'inline-flex' }}>
            <Button
              size="small"
              type="text"
              icon={<MailOutlined />}
              onClick={() => onEmailVersion(fileUid, versionId, fileName)}
              disabled={
                anyOperationInProgress || disabled || saveBlockedReason !== null
              }
              id={`attachment-email-button-${fileUid}-${versionId}`}
              aria-label={`Email ${fileName}`}
            />
          </span>
        </Tooltip>
      )}
      {isShared ? (
        <Popover
          content={
            <FlexCol
              id={`attachment-share-popover-content-${fileUid}-${versionId}`}
            >
              <FlexBox stretch justifyCenter>
                <Text>Link expires {formatExpirationDate(version.share)}</Text>
              </FlexBox>
              <FlexBox>
                <Button
                  size="small"
                  danger
                  type="link"
                  icon={<StopOutlined />}
                  onClick={() => onUnshareVersion(fileUid, versionId)}
                  disabled={
                    anyOperationInProgress ||
                    disabled ||
                    unresolvedReason !== null
                  }
                  id={`attachment-revoke-button-${fileUid}-${versionId}`}
                  aria-label={`Revoke share link for ${fileName}`}
                >
                  Revoke
                </Button>
                <Button
                  size="small"
                  type="text"
                  icon={<LinkOutlined />}
                  onClick={() => onCopyShareLink(fileUid, versionId)}
                  disabled={
                    anyOperationInProgress ||
                    disabled ||
                    unresolvedReason !== null
                  }
                  id={`attachment-copy-link-button-${fileUid}-${versionId}`}
                  aria-label={`Copy share link for ${fileName}`}
                >
                  Copy Link
                </Button>
              </FlexBox>
            </FlexCol>
          }
        >
          <Tooltip
            title={unresolvedReason ?? `Manage share options for ${fileName}`}
          >
            <span style={{ display: 'inline-flex' }}>
              <Button
                size="small"
                type="text"
                icon={<LinkOutlined />}
                onClick={() => {
                  if (version.share?.url) {
                    onCopyShareLink(fileUid, versionId);
                  } else {
                    onShareVersion(fileUid, versionId, fileName);
                  }
                }}
                disabled={
                  anyOperationInProgress ||
                  disabled ||
                  unresolvedReason !== null
                }
                id={`attachment-shared-button-${fileUid}-${versionId}`}
                aria-label={`Manage share options for ${fileName}`}
              />
            </span>
          </Tooltip>
          <Tag>Shared</Tag>
        </Popover>
      ) : (
        <Tooltip title={shareBlockedReason ?? 'Create share link'}>
          <span style={{ display: 'inline-flex' }}>
            <Button
              size="small"
              type="text"
              icon={<LinkOutlined />}
              onClick={() => onShareVersion(fileUid, versionId, fileName)}
              disabled={
                anyOperationInProgress ||
                disabled ||
                shareBlockedReason !== null
              }
              id={`attachment-share-button-${fileUid}-${versionId}`}
              aria-label={`Share ${fileName}`}
            />
          </span>
        </Tooltip>
      )}
    </FlexBox>
  );
};
